import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { logger } from '../lib/logger';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { LogOut } from 'lucide-react';
import GlobalSearch from '../components/GlobalSearch';

import KpiTab from './admin/KpiTab';
import ProductsTab from './admin/ProductsTab';
import QuotesTab from './admin/QuotesTab';
import BuyersTab from './admin/BuyersTab';
import AnalyticsTab from './admin/AnalyticsTab';
import ShareTab from './admin/ShareTab';
import TemplatesTab from './admin/TemplatesTab';
import AutomationsTab from './admin/AutomationsTab';
import StandardFeaturesTab from './admin/StandardFeaturesTab';
import AboutTab from './admin/AboutTab';

export default function AdminDashboard() {
  const { user, logout, checking } = useAuth();
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState({
    products: [], quotes: [], buyers: [], analytics: null,
    standardFeatures: { translations: {} },
    about: { translations: {}, contact_email: '', contact_phone: '', contact_address: '' },
  });

  const load = useCallback(async () => {
    try {
      const [p, q, sf, ab, b, an] = await Promise.all([
        api.get('/admin/products'),
        api.get('/admin/quote-requests'),
        api.get('/standard-features'),
        api.get('/about'),
        api.get('/admin/buyers'),
        api.get('/admin/analytics'),
      ]);
      setData({
        products: p.data, quotes: q.data, buyers: b.data,
        analytics: an.data,
        standardFeatures: sf.data || { translations: {} },
        about: ab.data || { translations: {}, contact_email: '', contact_phone: '', contact_address: '' },
      });
    } catch (err) {
      logger.warn('[AdminDashboard] Failed to load data:', err?.message);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (checking) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const newQuotesCount = data.quotes.filter(q => q.status === 'new').length;

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-[#E5D9C8] bg-white sticky top-0 z-30">
        <div className="max-w-[1440px] mx-auto px-6 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-6 flex-shrink-0">
            <Link to="/" className="font-serif text-lg tracking-[0.15em] uppercase">COSMETISSAGE<sup className="text-[0.5em] ml-0.5 text-[#C5A059] font-sans">®</sup></Link>
            <Badge variant="outline" className="text-xs uppercase tracking-widest">Admin</Badge>
          </div>
          <div className="flex-1 max-w-md">
            <GlobalSearch onNavigate={(target) => setTab(target)} />
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <span className="text-sm text-[#3D2817]/60 hidden md:inline">{user.email}</span>
            <Button data-testid="admin-logout" variant="ghost" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Products ({data.products.length})</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes">
              Quote Requests
              {newQuotesCount > 0 && <span className="ml-2 bg-[#C5A059] text-white text-xs rounded-full w-5 h-5 inline-flex items-center justify-center">{newQuotesCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="buyers" data-testid="tab-buyers">Buyers ({data.buyers.length})</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="share" data-testid="tab-share">Share</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
            <TabsTrigger value="automations" data-testid="tab-automations">Automations</TabsTrigger>
            <TabsTrigger value="standard" data-testid="tab-standard">Standard Features</TabsTrigger>
            <TabsTrigger value="about" data-testid="tab-about">About Us</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <KpiTab onNavigate={(target) => setTab(target)} />
          </TabsContent>
          <TabsContent value="products" className="mt-6">
            <ProductsTab products={data.products} onReload={load} />
          </TabsContent>
          <TabsContent value="quotes" className="mt-6">
            <QuotesTab quotes={data.quotes} onReload={load} />
          </TabsContent>
          <TabsContent value="buyers" className="mt-6">
            <BuyersTab buyers={data.buyers} products={data.products} onReload={load} />
          </TabsContent>
          <TabsContent value="analytics" className="mt-6">
            <AnalyticsTab analytics={data.analytics} />
          </TabsContent>
          <TabsContent value="share" className="mt-6">
            <ShareTab />
          </TabsContent>
          <TabsContent value="templates" className="mt-6">
            <TemplatesTab />
          </TabsContent>
          <TabsContent value="automations" className="mt-6">
            <AutomationsTab />
          </TabsContent>
          <TabsContent value="standard" className="mt-6">
            <StandardFeaturesTab key={JSON.stringify(data.standardFeatures)} initial={data.standardFeatures} onReload={load} />
          </TabsContent>
          <TabsContent value="about" className="mt-6">
            <AboutTab key={JSON.stringify(data.about)} initial={data.about} onReload={load} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
